import type { User } from '../types';
import { isAccountBannedError, type AccountBannedError } from '../types/errors';

/**
 * Redeem a web Google sign-in.
 *
 * The server's OAuth callback used to redirect here with `?token=<session
 * token>&user=...` — a bearer credential in the first page URL, which GA and
 * Sentry capture before React runs, and which lands in browser history,
 * Referer headers and the proxy access log. Now the redirect carries nothing:
 * the callback set an httpOnly cookie holding a single-use, two-minute code
 * scoped to the exchange route, and this POST (credentials: 'include') turns
 * it into the same JSON a password login returns. The native app still gets
 * its token in the nvcapp:// deep link and never calls this.
 */
export type GoogleSignInOutcome =
  | { kind: 'success'; token: string; user: User; isNewUser: boolean }
  | { kind: 'banned'; payload: AccountBannedError }
  | { kind: 'expired'; message: string }
  | { kind: 'failed'; message: string };

export const GOOGLE_SIGNIN_EXPIRED_MESSAGE = 'Sign-in expired, try again.';
export const GOOGLE_SIGNIN_FAILED_MESSAGE = 'Google login failed. Please try again.';

interface ExchangeBody {
  success?: boolean;
  data?: { token?: unknown; user?: unknown; is_new_user?: unknown };
}

async function exchange(): Promise<GoogleSignInOutcome> {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/google/exchange`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    const payload: unknown = await response.json().catch(() => null);

    if (response.status === 401) return { kind: 'expired', message: GOOGLE_SIGNIN_EXPIRED_MESSAGE };
    if (isAccountBannedError(payload)) return { kind: 'banned', payload };

    const body = payload as ExchangeBody | null;
    const token = body?.data?.token;
    const user = body?.data?.user;
    if (
      response.ok &&
      body?.success === true &&
      typeof token === 'string' &&
      token !== '' &&
      user &&
      typeof user === 'object'
    ) {
      return {
        kind: 'success',
        token,
        user: user as User,
        isNewUser: body.data?.is_new_user === true,
      };
    }
  } catch {
    /* offline or not JSON: a plain failure */
  }
  return { kind: 'failed', message: GOOGLE_SIGNIN_FAILED_MESSAGE };
}

/**
 * The code is single use, so a second POST always loses: React StrictMode's
 * double effect (and any re-run of the callback page's effect) must share the
 * one in flight rather than race it into "Sign-in expired".
 */
let inFlight: Promise<GoogleSignInOutcome> | null = null;

export function redeemGoogleSignIn(): Promise<GoogleSignInOutcome> {
  inFlight ??= exchange();
  return inFlight;
}

/** Test seam: forget the shared redemption. */
export function resetGoogleSignInForTests(): void {
  inFlight = null;
}
